import {useCountryConfiguration} from '@hconnect/common/hooks/useCountryConfiguration'
import {Avatar, LabelRegular, MenuItemConfig, UserMenu} from '@hm/fundamental'
import {mdiTranslate, mdiLogout} from '@mdi/js'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined'
import React, {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {useHistory} from 'react-router-dom'

import {loginFlow} from '../App.store'
import {useGetLegalLinks} from '../common/hooks/useGetLegalLinks'
import {useUpdateUserData} from '../common/hooks/useUpdateUserData'
import {useTranslation} from '../hooks/useTranslation'
import {selectLoggedInUserProfile} from '../modules/LoggedInUser.selectors'

export const MHCUserMenu = () => {
  const loggedInUserProfile = useSelector(selectLoggedInUserProfile)
  const {t, i18n} = useTranslation()

  const history = useHistory()
  const legalLinks = useGetLegalLinks(loggedInUserProfile?.country || '')
  const displayNames = new Intl.DisplayNames([i18n.language], {type: 'language'})
  const {data: country} = useCountryConfiguration(loggedInUserProfile?.country)
  const {mutate} = useUpdateUserData()
  const menuItems = useMemo(
    (): MenuItemConfig[] => [
      ...(loggedInUserProfile
        ? [
            {
              key: 'user',
              title: loggedInUserProfile.name ?? '',
              customIcon: <Avatar name={loggedInUserProfile.name ?? ''} />,
              subtitle: loggedInUserProfile.eMail ?? '',
              onClicked: () => history.push(`/manage/${loggedInUserProfile?.user_id}`),
              closeOnClick: true
            }
          ]
        : []),
      ...(loggedInUserProfile
        ? [
            {
              key: 'language',
              title: displayNames.of(loggedInUserProfile?.defaultLocale?.split('-')[0] || 'en'),
              icon: mdiTranslate,
              ...(country && country?.locales.length > 1
                ? {
                    items: country.locales.map((locale) => ({
                      key: locale.code,
                      title: displayNames.of(locale.code.split('-')[0] || 'en'),
                      onClicked: (key: string) =>
                        mutate({
                          userId: loggedInUserProfile?.user_id,
                          userProp: {defaultLocale: key}
                        }),
                      closeOnClick: true
                    }))
                  }
                : [])
            }
          ]
        : []),
      {
        key: 'legal',
        title: t('actionBar.legal.title'),
        customIcon: <PolicyOutlinedIcon style={{color: '#54708C', fontSize: 18}} />,
        items: [
          {
            key: 'imprint',
            title: t('actionBar.legal.imprint'),
            customIcon: <ArticleOutlinedIcon style={{color: '#54708C', fontSize: 18}} />,
            onClicked: () => window.open(legalLinks.imprintLink, '_blank')
          },
          {
            key: 'tc',
            title: t('actionBar.legal.tc'),
            customIcon: <ArticleOutlinedIcon style={{color: '#54708C', fontSize: 18}} />,
            onClicked: () => window.open(legalLinks.tcLink, '_blank')
          },
          {
            key: 'oss',
            title: t('actionBar.legal.oss'),
            customIcon: <ArticleOutlinedIcon style={{color: '#54708C', fontSize: 18}} />,
            onClicked: () => window.open(legalLinks.ossLink, '_blank')
          },
          {
            key: 'dpp',
            title: t('actionBar.legal.dpp'),
            customIcon: <ArticleOutlinedIcon style={{color: '#54708C', fontSize: 18}} />,
            onClicked: () => window.open(legalLinks.dppLink, '_blank')
          }
        ]
      },
      ...(loggedInUserProfile
        ? [
            {
              key: 'logout',
              title: t('actionBar.logout'),
              icon: mdiLogout,
              onClicked: () => loginFlow.startLogoutProcess()
            }
          ]
        : [])
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loggedInUserProfile, t, country]
  )

  return (
    <UserMenu
      loggedIn={!!loggedInUserProfile}
      items={menuItems}
      footer={
        <div className="flex flex-col items-center">
          <LabelRegular className="text-foreground_faded">{t('actionBar.copyright')}</LabelRegular>
          <LabelRegular className="text-foreground_faded">
            <a href="https://heidelbergmaterials.com">
              {t('actionBar.heidelbergmaterials', {
                date: new Date().getFullYear()
              })}
            </a>
          </LabelRegular>
        </div>
      }
    />
  )
}
